import { Injectable } from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { NoneValueHelper } from '../../core/';
import { FilterFieldValue, FilterFieldType } from '../covance-filter.models';
import { isEmpty, isArray, forIn, uniq, min, max } from 'lodash';
import { CovanceFilterMultiSelectType } from './covance-filter-multiselect-type';

export type ChainNodeQueryValue = [string, string, string[]];

export enum PositionType {
    Pre,
    Post
}

interface ParsedChainIndex {
    chainIndex?: number;
}

@Injectable()
export class CovanceFilterChainNodeType extends CovanceFilterMultiSelectType {
    constructor(router: Router, route: ActivatedRoute, noneValueHelper: NoneValueHelper) {
        super(router, route, noneValueHelper);
    }

    public tryParseChainIndex(value: string, result?: ParsedChainIndex): boolean {
        const parsed = parseInt(value, 10);

        if (isNaN(parsed)) {
            return false;
        }

        if (result) {
            result.chainIndex = parsed;
        }

        return true;
    }

    public parseQueryValue(rawValue: string): ChainNodeQueryValue | null {
        try {
            const [title, filteredFieldName, values] = JSON.parse(rawValue) as ChainNodeQueryValue;

            if (isEmpty(title) || isEmpty(filteredFieldName)) {
                return null;
            }

            return [title, filteredFieldName, isArray(values) ? values : []];
        } catch (e) {
            return null;
        }
    }

    public setValue(fieldVal: FilterFieldValue) {
        const chainNode = this.parseQueryValue(fieldVal.queryParamValue);

        if (!chainNode) {
            return;
        }

        const [, , values] = chainNode;

        fieldVal.value = values;
    }

    public setItems(fieldVal: FilterFieldValue, entities: any[]) {
        let data = fieldVal.def.getItems ? fieldVal.def.getItems(entities) :
            uniq(entities.map((entity: any) => entity[fieldVal.def.queryParam]));

        fieldVal.items = data;
    }

    public setQueryParamValue(fieldVal: FilterFieldValue) {
        fieldVal.queryParamValue = JSON.stringify([fieldVal.def.title, fieldVal.filteredFieldName, !isEmpty(fieldVal.value) ? fieldVal.value : []]);
    }

    public onFieldChange(fieldVal: FilterFieldValue) {
        const queryParams: Params = {};

        queryParams[fieldVal.def.queryParam] = this.encodeQueryParam(fieldVal.queryParamValue);

        forIn(this.route.snapshot.queryParams, (value: string, key: string) => {
            if (key !== fieldVal.def.queryParam) {
                queryParams[key] = value;
            }
        });

        this.router.navigate([], {
            queryParams: queryParams,
            relativeTo: this.route
        });
    }

    public onFieldAdd(fieldVal: FilterFieldValue, position: PositionType) {
        const queryParams: Params = {};
        const chainsIndexes: number[] = [];

        forIn(this.route.snapshot.queryParams, (paramValue: string, paramKey: string) => {
            queryParams[paramKey] = paramValue;

            const result: ParsedChainIndex = {};

            if (this.tryParseChainIndex(paramKey, result)) {
                chainsIndexes.push(result.chainIndex);
            }
        });

        let newChainIndex = null;

        if (position === PositionType.Pre) {
            newChainIndex = chainsIndexes.length === 0 ? 0 : min(chainsIndexes) - 1;
        } else if (position === PositionType.Post) {
            newChainIndex = chainsIndexes.length === 0 ? 0 : max(chainsIndexes) + 1;
        }

        if (newChainIndex === null) {
            return;
        }

        fieldVal.def.queryParam = newChainIndex.toString();
        queryParams[fieldVal.def.queryParam] = fieldVal.queryParamValue;

        this.router.navigate([], {
            queryParams: queryParams,
            relativeTo: this.route
        });
    }

    public createFilterFieldValue(queryParam: string, title: string, filteredFieldName: string): FilterFieldValue {
        const fieldVal: FilterFieldValue = {
            def: {
                title: title,
                queryParam: queryParam,
                type: FilterFieldType.ChainNode
            },
            filteredFieldName: filteredFieldName
        };

        return fieldVal;
    }

    public deleteFieldVal(fieldVal: FilterFieldValue) {
        const queryParams: Params = {};

        forIn(this.route.snapshot.queryParams, (value: string, key: string) => {
            if (key !== fieldVal.def.queryParam) {
                queryParams[key] = value;
            }
        });

        this.router.navigate([], {
            queryParams: queryParams,
            relativeTo: this.route
        });
    }

    public encodeQueryParam(queryParam: string): string | null {
        try {
            // Chrome: clip-path attribute doesn't work, when url() contains parenthesis. So, decode it manualy.
            return encodeURIComponent(queryParam).replace(/\(/g, '%28').replace(/\)/g, '%29');
        } catch (e) { }

        return null;
    }

    public decodeQueryParam(queryParam: string): string | null {
        try {
            return decodeURIComponent(queryParam);
        } catch (e) { }

        return null;
    }
}
